import { cx } from '../ui/classNames';
import {
    alignClassNames,
    bodyCellClassName,
    cellPaddingClassNames,
    lastPinnedClassName,
    stickyCellClassNames,
    truncateClassName,
} from './tableClasses';

// DataTable's own leading checkbox column (see DataTable `rowSelection`).
export const SELECT_COLUMN_ID = '__select';
export const SELECT_COLUMN = Object.freeze({
    id: SELECT_COLUMN_ID,
    size: 44,
    enableSorting: false,
    enableHiding: false,
    meta: Object.freeze({ align: 'center', label: 'Select rows' }),
});

// TanStack's default column size, for fixed-layout columns that set none.
const DEFAULT_COLUMN_WIDTH = 150;
const EMPTY_OBJECT = Object.freeze({});

/**
 * A column's plain-text name: its string `header`, else `meta.label`, else
 * its id. Takes a column def (and its id when the def has none).
 */
export function getColumnLabel(columnDef, id = columnDef.id) {
    const { header, meta } = columnDef;
    return typeof header === 'string' ? header : (meta?.label ?? id);
}

/**
 * One entry per visible leaf column in render order (pinned first, as
 * TanStack orders its headers), with everything a cell needs that does not
 * change from row to row:
 * - width: the <col> width (px number or CSS string), null for columns that
 *   take a share of the leftover width (`meta.grow`, or no size in the auto
 *   layout)
 * - stickyLeft: the left offset of a pinned column, from the px widths of
 *   the pinned columns before it
 * - bodyClassName / summaryClassName: the static classes of its body cells
 *   and of its cells in sticky summary rows
 *
 * Also returns the table's `minWidth` (the sum of each column's minimum: its
 * px width, else `minSize ?? size`), whether a filler column should absorb
 * the leftover width (only when every width is a fixed px width), and
 * `byId`.
 */
export function buildColumnLayout(leafHeaders, pinnedCount, autoLayout) {
    let left = 0;
    let minWidth = 0;
    let flexible = autoLayout;
    const entries = leafHeaders.map((header, index) => {
        const { column } = header;
        const { meta = EMPTY_OBJECT, size, minSize } = column.columnDef;
        let width = null;
        if (!meta.grow) width = meta.width ?? size ?? (autoLayout ? null : DEFAULT_COLUMN_WIDTH);
        if (meta.grow || typeof width === 'string') flexible = true;
        minWidth += typeof width === 'number' ? width : (minSize ?? size ?? DEFAULT_COLUMN_WIDTH);
        const pinned = index < pinnedCount;
        const stickyLeft = pinned ? left : undefined;
        if (pinned && typeof width === 'number') left += width;
        const isSelect = column.id === SELECT_COLUMN_ID;
        const align = meta.align || 'left';
        const cellClassName = cx(
            bodyCellClassName,
            isSelect ? cellPaddingClassNames.none : cellPaddingClassNames.normal,
            alignClassNames[align],
            !isSelect && !meta.wrap && truncateClassName,
            pinned && index === pinnedCount - 1 && lastPinnedClassName,
        );
        return {
            id: column.id,
            column,
            header,
            width,
            align,
            pinned,
            stickyLeft,
            lastPinned: pinned && index === pinnedCount - 1,
            bodyClassName: cx(cellClassName, pinned && stickyCellClassNames.pinned),
            summaryClassName: cx(
                cellClassName,
                pinned ? stickyCellClassNames.summaryPinned : stickyCellClassNames.summary,
            ),
        };
    });
    return {
        entries,
        byId: new Map(entries.map((entry) => [entry.id, entry])),
        minWidth,
        filler: !flexible,
    };
}

/**
 * The header rows from TanStack's header groups, as the cells to render:
 * a leaf column outside any group is one cell spanning every header row
 * below its first appearance (TanStack repeats it as placeholders);
 * groups span their leaf columns; a group's placeholder above a shallower
 * group renders blank.
 */
export function buildHeaderRows(headerGroups) {
    const placed = new Set();
    return headerGroups.map((group, depth) => {
        const cells = [];
        group.headers.forEach((header) => {
            if (header.column.columns.length === 0) {
                if (placed.has(header.column.id)) return;
                placed.add(header.column.id);
                cells.push({ header, kind: 'leaf', rowSpan: headerGroups.length - depth, colSpan: 1 });
                return;
            }
            cells.push({
                header,
                kind: header.isPlaceholder ? 'blank' : 'group',
                rowSpan: 1,
                colSpan: header.colSpan,
            });
        });
        return { id: group.id, depth, cells };
    });
}
